import MeEntity from "../Entities/MeEntity";
import Network from "../network/network";
import CompanyEntity from "../Entities/CompaniesEntity";
import Util from "../util/Util";
import MeService from "../services/MeService";
import SessionService, { Session } from "../services/SessionService";
import Permissions from "../models/Permissions";
import AkquiseTypeService from "../services/AkuiseTypeService";
import AvatarService from "../services/AvatarService";
import CurrencyService from "../services/CurrencyService";
import InstitutionAddressService from "../services/InstitutionAddressService";
import InstitutionContactService from "../services/InstitutionContactService";
import InstitutionService from "../services/InstitutionService";
import PersonAddressService from "../services/PersonAddressService";
import PersonContactService from "../services/PersonContactService";
import PersonService from "../services/PersonService";
import PersonToInstitutionRelationService from "../services/PersonToInstitutionRelationService";
import SenderModelService from "../services/SenderModelService";
import AttachementService from "../services/AttachementService";
import AkquiseService from "../services/AkquiseService";
import OpportunityService from "../services/OpportunityService";
import TodoService from "../services/TodoService";
import OpportunityModel from "../models/OpportunityModel";
import PersonToInstitutionRelationModel from "../models/PersonToInstitutionRelationModel";
import SenderModel from "../models/SenderModel";
import OpportunityEntity from "../Entities/OpportunityEntity";
import CurrencyEntity from "../Entities/CurrencyEntity";
import TodoModel from "../models/TodoModel";
import TodoEntity from "../Entities/TodoEntity";
import ContactNoteModel from "../models/contactNoteModel";
import AkquiseTypeEntity from "../Entities/AkquiseTypeEntity";
import { EntityTypeId } from "../util/EntityTypeId";
import ApiError from "../network/ApiError";
import Logger from "../util/Logger";
import { Loglevel } from "../util/Logger";
import ContactNoteEntity from "../Entities/ContactNoteEntity";
import SearchService from "../services/SearchService";
import PersonToInstitutionRelationEntity from "../Entities/PersonToInstitutionRelationEntity";
import PersonToInstRelationTypeEntity from "../Entities/PersonToInstRelationTypeEntity";
import ContactMediaService from "../services/ContactMediaService";
import IApiSearchResult from "../interfaces/IApiSearchResult";
import Translation from "../services/TranslationService";
import AddressParserService from "../services/AddressParserService";
import PersonToInstRelationTypeService from "../services/PersonToInstRelationTypeService";
import SalutationService from "../services/SalutationService";
import ContactCreationService from "../services/ContactCreationService";

export default abstract class BaseModel extends kendo.data.ObservableObject {
    protected initialized: boolean;
    protected meService: MeService;
    protected network: Network;
    protected logger: Logger;
    protected ptirService: PersonToInstitutionRelationService;
    protected personService: PersonService;
    protected institutionService: InstitutionService;
    protected akquiseTypeService: AkquiseTypeService;
    protected contactNoteService: AkquiseService;
    protected senderModelService: SenderModelService;
    protected currencyService: CurrencyService;
    protected avatarService: AvatarService;
    protected todoService: TodoService;
    protected attachementService: AttachementService;
    protected opportunityService: OpportunityService;
    protected searchService: SearchService;
    protected contactMediaTypeService: ContactMediaService;
    protected addressParserService: AddressParserService;
    public contactCreationService: ContactCreationService;

    private meEntity: MeEntity;
    protected sessionService: SessionService;
    /** Every right granted until the session is known, and for a load whose session failed - see loadSession. */
    public permissions: Permissions;

    private companyChooserDialog: kendo.ui.Confirm;
    private personInstitutionChooser: kendo.ui.Confirm;

    protected selectedContactNotesOpportunityId: number;
    protected selectedTodoOpportunityId: number;
    protected todoValidator: kendo.ui.Validator;
    private contactNotevalidator: kendo.ui.Validator;
    private token: any;
    private currencies: CurrencyEntity[];
    private akquiseTypes: AkquiseTypeEntity[];

    private opportunitySearchEnabledForContactNotes: boolean;
    private opportunitySearchEnabledForTodos: boolean;
    private static translation = new Translation();
    private searchTimeout = null;

    public get<T>(name: string): T {
        return super.get(name) as T;
    }

    public readonly PAGE_SIZE = 15;

    public newContactLink: string = `${Network.BaseURL}PersonDetail/Details/0?editMode=True`;
    public newOpportunityLink: string = `${Network.BaseURL}OpportunityDetail/Details/0?editMode=True`;
    public newTodoLink: string = `${Network.BaseURL}TodoDetail/Details/0?editMode=True`;

    constructor(network: Network) {
        super();
        super.init(this);
        this.network = network;
        this.initServices();
        this.addListener();
        this.resetDefaultValues();
    }

    protected abstract getSubject(): Promise<string>;

    protected abstract getEmailAddress(allowShowChooser: boolean): Promise<string>;

    protected abstract setAttachments(): Promise<void>;

    protected abstract handleAttachmentUpload(uuid: string, entityTypeId: EntityTypeId): Promise<boolean>;

    protected abstract ccAndToButtonsVisible(): boolean;

    protected abstract addParticipantButtonVisible(): boolean;

    protected abstract addContactNoteCategory(uuid: string);
    protected abstract contactNoteBoundToMail(uuid: string): boolean;

    protected abstract addTodoCategory(uuid: string);
    protected abstract todoBoundToMail(uuid: string): boolean;

    protected logoutInvisible(): boolean {
        return false;
    }

    protected referencesTabVisible(): boolean {
        return false;
    }

    /**
     * Camera capture. Not possible in the Office task pane: Outlook sets an allow attribute
     * without "camera" on the add-in iframe, and capture only works on touch devices anyway.
     */
    protected cameraCaptureVisible(): boolean {
        return false;
    }

    /**
     * Live capture via getUserMedia. Blocked in the Office task pane (see above) and
     * unnecessary on touch devices, where capture yields the better photo.
     */
    protected webcamCaptureAllowed(): boolean {
        return false;
    }

    /** Sets webcamAvailable once it is known whether a camera exists. */
    protected async probeWebcam(): Promise<void> {
        this.set("webcamAvailable", false);
        if (!this.webcamCaptureAllowed()) {
            return;
        }

        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const hasCamera = devices.some(d => d.kind === "videoinput");
            this.set("webcamAvailable", hasCamera);
        } catch (error) {
            this.set("webcamAvailable", false);
        }
    }

    public initServices(): void {
        this.logger = new Logger(this.network);

        this.meService = new MeService(this.network);
        this.sessionService = new SessionService(this.network);
        // Not a field initializer: kendo's init would turn the instance into a plain observable copy.
        this.setPermissions(new Permissions());
        this.avatarService = new AvatarService(this.network, this.logger);

        this.akquiseTypeService = new AkquiseTypeService(this.network, this.logger);
        this.currencyService = new CurrencyService(this.network, this.logger);
        this.attachementService = new AttachementService(this.network, this.logger);

        const personContactService = new PersonContactService(this.network, this.logger);
        const personAddressService = new PersonAddressService(this.network, this.logger);

        const institutionContactService = new InstitutionContactService(this.network, this.logger);
        const institutionAddressService = new InstitutionAddressService(this.network, this.logger);

        this.todoService = new TodoService(this.network, this.logger);
        this.opportunityService = new OpportunityService(this.network, this.currencyService, this.logger);

        this.contactNoteService = new AkquiseService(this.network, this.logger);

        this.contactMediaTypeService = new ContactMediaService(this.network, this.logger);
        this.ptirService = new PersonToInstitutionRelationService(this.network, this.contactMediaTypeService, this.logger);

        this.personService = new PersonService(
            this.network,
            personContactService,
            personAddressService,
            this.avatarService,
            this.contactMediaTypeService,
            this.logger
        );
        this.institutionService = new InstitutionService(
            this.network,
            institutionContactService,
            institutionAddressService,
            this.avatarService,
            this.contactMediaTypeService,
            this.logger
        );
        this.senderModelService = new SenderModelService(this.personService, this.institutionService, this.ptirService);

        this.searchService = new SearchService(this.network, this.logger);
        this.addressParserService = new AddressParserService(this.network);
        const ptirTypeService = new PersonToInstRelationTypeService(this.network, this.logger);
        const salutationService = new SalutationService(this.network, this.logger);

        this.contactCreationService = new ContactCreationService(
            ptirTypeService,
            salutationService,
            this.addressParserService,
            BaseModel.t,
            async (personId, institutionId) => {
                // Callback after contact creation - load the created contact using the full load method
                await this.load(null, personId, institutionId, false);
            },
            (message, level) => {
                // Notification callback
                this.showNotification(message, level === "error" ? "warning" : level);
            }
        );

        // Sync service visibility changes with BaseModel
        this.contactCreationService.bind("change", e => {
            if (e.field === "createContactPhotoVisible") {
                this.set("createContactPhotoVisible", this.contactCreationService.get("createContactPhotoVisible"));
            }
            if (e.field === "createContactTextVisible") {
                this.set("createContactTextVisible", this.contactCreationService.get("createContactTextVisible"));
            }
            if (e.field === "createContactReviewVisible") {
                this.set("createContactReviewVisible", this.contactCreationService.get("createContactReviewVisible"));
            }
            if (e.field === "selectedPhotoFile" || e.field === "canAnalyzePhoto") {
                this.set("canAnalyzePhoto", this.contactCreationService.canAnalyzePhoto);
            }
            if (e.field === "contactTextInput") {
                this.set("canAnalyzeText", this.contactCreationService.canAnalyzeText);
            }
            if (e.field === "parsedPersons") {
                this.set("parsedPersons", this.contactCreationService.get("parsedPersons"));
            }
            if (e.field === "parsedInstitutions") {
                this.set("parsedInstitutions", this.contactCreationService.get("parsedInstitutions"));
            }
            if (e.field === "parsedRelation") {
                this.set("parsedRelation", this.contactCreationService.get("parsedRelation"));
            }
            if (e.field === "hasRelation") {
                this.set("hasRelation", this.contactCreationService.get("hasRelation"));
            }
            if (e.field === "unassignedContactData") {
                this.set("unassignedContactData", this.contactCreationService.get("unassignedContactData"));
            }
            if (e.field === "canCreateContact") {
                this.set("canCreateContact", this.contactCreationService.get("canCreateContact"));
            }
            if (e.field === "relationTypes") {
                this.set("relationTypes", this.contactCreationService.get("relationTypes"));
            }
            if (e.field === "selectedRelationTypeId") {
                this.set("selectedRelationTypeId", this.contactCreationService.get("selectedRelationTypeId"));
            }
            if (e.field === "salutations") {
                this.set("salutations", this.contactCreationService.get("salutations"));
            }
            if (e.field === "selectedFormalityId") {
                this.set("selectedFormalityId", this.contactCreationService.get("selectedFormalityId"));
            }
            if (e.field === "canAddRelation") {
                this.set("canAddRelation", this.contactCreationService.get("canAddRelation"));
            }
            if (e.field === "webcamActive") {
                this.set("webcamActive", this.contactCreationService.get("webcamActive"));
            }
        });
    }

    public async init(): Promise<void> {
        await BaseModel.translation.init();
        // Before kendo.bind, so the webcam button visibility is correct from the start
        await this.probeWebcam();
    }

    public login = () => {
        this.chooseCompany();
    };

    public getMe = async (): Promise<MeEntity> => {
        if (!this.meEntity) {
            this.meEntity = await this.sessionService.getMe();
        }
        return this.meEntity;
    };

    /**
     * Fetched before anything else is loaded, as the rights decide what is. Outlook opens the taskpane straight
     * into load(), without the company chooser that asks for the account on the web.
     */
    private loadSession = async (): Promise<void> => {
        try {
            this.applySession(await this.sessionService.getSession());
        } catch (e) {
            // A failed request like any other during the load: the requests go out and the backend decides. Kept
            // nowhere, so the next load asks again; getMe asks for the account once it is needed.
            this.logger.log(Loglevel.Warning, "Could not load the session, so the backend decides on every request", e);
            this.meEntity = null;
            this.setPermissions(new Permissions());
        }
    };

    private applySession = (session: Session): void => {
        // A kept session carries no account; getMe then asks for it once it is needed, as it always did.
        if (session.me) {
            this.meEntity = session.me;
        }
        this.setPermissions(session.permissions);
    };

    /** The views hide what is denied through the "denied" class, which outranks their own visible bindings. */
    private setPermissions = (permissions: Permissions): void => {
        this.permissions = permissions;
        const denied: { [right: string]: boolean } = { ContactCreate: !permissions.ContactCreate };
        Object.keys(permissions).forEach(right => (denied[right] = !permissions[right]));
        this.set("denied", denied);
    };

    public async load(email?: string, personId?: number, institutionId?: number, allowShowChooser: boolean = true) {
        this.companyChooserDialog?.close();
        this.personInstitutionChooser?.close();
        ($("#orphySaveMail") as any).modal("hide");
        ($("#orphyTodo") as any).modal("hide");
        this.set("searchVisible", false);
        this.set("relationChooserVisible", false);
        this.set("hasAttachedTodoOpportunity", false);
        this.set("todoPriorities", this.getTodoPriorites());
        this.set("reminderFactor", this.getReminderFactor());
        this.set("smilies", this.getSmilies());

        try {
            Util.showLoading();
            this.resetDefaultValues();

            this.token = await this.network.authenticate();

            // Ensure we have a valid token before proceeding with any data operations
            if (!this.token || !this.token.access_token) {
                throw new Error("No valid authentication token available");
            }

            this.setListViewHeight();
            await this.loadSession();

            // Only what the user may read is asked for: a refused list answers every load with a 403.
            this.akquiseTypes = this.permissions.ContactNoteRead || this.permissions.ContactNoteWrite ? await this.akquiseTypeService.getItems() : [];
            this.currencies = this.permissions.LeadRead || this.permissions.LeadCreate ? await this.currencyService.getItems() : [];

            let emailAddress = email ? email : await this.getEmailAddress(allowShowChooser);
            this.set("emailAddress", emailAddress);
            this.set("personId", personId);
            this.set("institutionId", institutionId);
            this.setNewLinks(emailAddress);
            await this.loadData(emailAddress, personId, institutionId);

            this.set("globalSearchDataSource", this.getSearchDataSource());

            const $list = $("#searchListView");
            const existingListView = $list.data("kendoMobileListView");
            if (existingListView) {
                existingListView.destroy();
                $list.empty();
            }

            $("#searchListView")
                .kendoMobileListView({
                    dataSource: this.get("globalSearchDataSource"),
                    template: $("#searchTemplate").html(),
                    click: e => this.manualSelectContact(e),
                    dataBound: e => this.searchListViewDataBound(e),
                })
                .data().kendoMobileListView;

            this.set("senderVisible", !!this.get("sender.Person") || !!this.get("sender.Institution"));
            this.set("searchVisible", !this.get("senderVisible"));
            this.set("showContent", true);

            this.selectedContactNotesOpportunityId = null;
            this.selectedTodoOpportunityId = null;

            this.set(
                "filterInstOpportunities",
                this.get("sender") && this.get<SenderModel>("sender").HasInstitution ? this.get<SenderModel>("sender").HasInstitution : false
            );
            this.set("openOpportunitiesForContactNotes", this.dataSourceIf(this.permissions.LeadRead, () => this.getOpenOpportunityDataSource(this.getFilterForOpportunitiesInContactNotes)));
            this.set("openOpportunitiesForTodos", this.dataSourceIf(this.permissions.LeadRead, () => this.getOpenOpportunityDataSource(this.getFilterForOpportunitiesInTodos)));

            // The responsible users are only picked when a task or a lead is created.
            this.set("todoVerantwortliche", this.dataSourceIf(this.permissions.TodoCreate || this.permissions.LeadCreate, () => this.getVerantwortlicheDataSource()));
            await this.setAttachments();
            await this.setInitialContactNotesGuiValues();
            await this.setInitialTodoGuiValues();
            await this.setInitialOpportunityGuiValues();
            this.set("aTypes", this.akquiseTypes);
            this.contactNotevalidator = $("#contactNoteInputWrapper").kendoValidator().data().kendoValidator;
            this.todoValidator = $("#todoInputWrapper").kendoValidator().data().kendoValidator;
            $("#tabs").data("kendoTabStrip").select(0);
            BaseModel.localize();
            Util.hideLoading();
        } catch (e) {
            this.logger.log(Loglevel.Info, `No contact with email address ${this.get("emailAddress")} found in orphy`, e);
            $("#tabs").data("kendoTabStrip").select(0);
            this.set("showContent", true);
            Util.hideLoading();
            this.showSearchView();
        }
    }

    public static localize = () => {
        try {
            BaseModel.translation.localize("body");
        } catch (e) {
            console.error(`could not localize body: ${e}`);
        }
    };

    public static t(key: string, args?: any): string {
        try {
            return BaseModel.translation.t(key, args) as string;
        } catch (e) {
            console.error(`could not translate key: ${args} ${e}`);
            return "";
        }
    }

    private getTodoPriorites() {
        return [
            { text: BaseModel.t("priority-non"), value: "Prio0" },
            { text: BaseModel.t("priority-very-low"), value: "Prio1" },
            { text: BaseModel.t("priority-low"), value: "Prio2" },
            { text: BaseModel.t("priority-medium"), value: "Prio3" },
            { text: BaseModel.t("priority-high"), value: "Prio4" },
            { text: BaseModel.t("priority-very-high"), value: "Prio5" },
        ];
    }

    private getReminderFactor = () => {
        return [
            { name: BaseModel.t("minute", { count: 0 }), factor: 60 },
            { name: BaseModel.t("hour", { count: 0 }), factor: 60 * 60 },
            { name: BaseModel.t("day", { count: 0 }), factor: 60 * 60 * 24 },
            { name: BaseModel.t("week", { count: 0 }), factor: 60 * 60 * 24 * 7 },
        ];
    };

    private getSmilies = () => [
        { smiley: "happy", value: "Happy" },
        { smiley: "neutral", value: "Neutral" },
        { smiley: "sad", value: "Unhappy" },
    ];

    private addListener = () => {
        $("#searchListView").on("click", ".searchListViewHeaderSelector", this.hideSearchGroup);
        // Open images embedded in a contact note at full size in a new tab (allows download).
        $(document).on("click", ".note-content img", e => {
            const src = $(e.currentTarget).attr("src");
            if (!src) {
                return;
            }
            // Browsers block opening a data: URI as a top-level tab (blank window),
            // so convert embedded base64 images to a Blob URL first.
            if (src.indexOf("data:") === 0) {
                try {
                    const [meta, base64] = src.split(",");
                    const mime = (meta.match(/data:(.*?)(;|$)/) || [])[1] || "image/png";
                    const bytes = atob(base64);
                    const arr = new Uint8Array(bytes.length);
                    for (let i = 0; i < bytes.length; i++) {
                        arr[i] = bytes.charCodeAt(i);
                    }
                    const blobUrl = URL.createObjectURL(new Blob([arr], { type: mime }));
                    window.open(blobUrl, "_blank");
                    return;
                } catch (err) {
                    this.logger.log(Loglevel.Error, "Could not open note image", err);
                }
            }
            window.open(src, "_blank");
        });
    };

    public reloadPlugin = async () => {
        await this.load();
    };

    public refreshPlugin = async () => {
        await this.load(this.get("emailAddress"), this.get("personId"), this.get("institutionId"));
    };

    public openOrphyNew = e => {
        window.open($(e.currentTarget).data("link"), "_blank");
    };

    protected getCreateContactNoteText = (): string => {
        return "create-contact-note";
    };

    public toggleCreateContactNote = () => {
        this.set("createContactNoteVisible", !this.get("createContactNoteVisible"));
    };

    public toggleCreateTodo = () => {
        this.set("createTodoVisible", !this.get("createTodoVisible"));
    };

    public reminderActiveChanged = (): void => {
        if (!this.get("reminderActive")) {
            this.set("reminderSeconds", 0);
        }
    };

    public chooseCompany = async (): Promise<void> => {
        let me = await this.getMe();
        if (!this.get("initialized")) {
            if (me.Companies.length > 1) {
                const companyId = await this.selectCompany(me.Companies, me.CompanyId);
                if (me.CompanyId !== companyId) {
                    try {
                        await this.meService.switchCompany(companyId);
                    } catch (e) {
                        // A refused switch used to end the sign in right here, with an empty taskpane and
                        // nothing said. It loads in the company the user is still in instead.
                        this.logger.log(Loglevel.Error, "Could not switch the company, staying in the current one", e);
                    }
                }
            }
            this.set("initialized", true);
            await this.load();
        }
        await this.getMe();
    };

    private selectCompany = (companies: CompanyEntity[], currentCompanyId: number): Promise<number> => {
        $("body").append("<div id='confirmTemplate'></div>");
        let content = "<select style='width:100%'>";
        companies.forEach(c => (content += `<option ${currentCompanyId === c.Id ? "selected" : ""} value=${c.Id}>${c.Name}</option>`));
        content += "</select>";
        return new Promise(res => {
            this.companyChooserDialog = $("#confirmTemplate")
                .kendoConfirm({
                    title: BaseModel.t("choose-company"),
                    content: content,
                })
                .data("kendoConfirm");

            return this.companyChooserDialog
                .open()
                .result.done(() => res(Number($("#confirmTemplate").find(":selected").val())))
                .fail(() => res(Util.firstOrDefault(companies).Id))
                .always(() => $("#confirmTemplate").remove());
        });
    };

    public signout = () => {
        this.network.signout();
        // The next user signing in to this taskpane must not inherit this one's account or rights.
        this.meEntity = null;
        this.setPermissions(new Permissions());
        this.set("initialized", false);
    };

    private setListViewHeight = () => {
        const height = $("#app-body").height() - $("#tabs > ul").height() - $(".headerTitle").height() - 2;

        // Card-header height based on CSS: padding (1rem top + 1rem bottom) + line-height + border (2px)
        // Using fixed value because cards might not be rendered yet when this is called
        const CARD_HEADER_HEIGHT = 65;

        $("#contactNoteListView").css("height", height - CARD_HEADER_HEIGHT);
        $("#opportunityListView").css("height", height - CARD_HEADER_HEIGHT);
        $("#todoListView").css("height", height - CARD_HEADER_HEIGHT);

        // Search list needs to account for card-header + search input + padding
        const SEARCH_INPUT_HEIGHT = 65; // Input height + padding
        $("#searchListView").css("height", height - CARD_HEADER_HEIGHT - SEARCH_INPUT_HEIGHT);
    };

    private getOpenOpportunityDataSource = (getFilterFunction: Function) => {
        return kendo.data.DataSource.create({
            type: "odata-v4",
            pageSize: this.PAGE_SIZE,
            serverPaging: true,
            serverFiltering: true,
            transport: {
                read: {
                    url: Network.OdataBaseURL + OpportunityService.ODATA_ROUTE + "?$expand=Person,ContactPerson,Institution",
                    beforeSend: req => {
                        req.setRequestHeader("Authorization", this.network.getAuthorizationHeader(this.token));
                        req.setRequestHeader("pragma", "no-cache");
                        req.setRequestHeader("content-type", "application/json");
                        req.setRequestHeader("cache-control", "no-cache");
                    },
                },
            },
            schema: {
                data: response => {
                    return response.value.map(l => new OpportunityModel(OpportunityEntity.fromOdata(l, new OpportunityEntity()), this.currencies));
                },
            },
            filter: getFilterFunction(),
        });
    };

    private getOpportunityDataSource = (filter: string) => {
        return kendo.data.DataSource.create({
            type: "odata-v4",
            pageSize: this.PAGE_SIZE,
            serverPaging: true,
            serverFiltering: true,
            transport: {
                read: {
                    url: Network.OdataBaseURL + OpportunityService.ODATA_ROUTE + `?$expand=Person,ContactPerson,Institution&$filter=${filter}`,
                    beforeSend: req => {
                        req.setRequestHeader("Authorization", this.network.getAuthorizationHeader(this.token));
                        req.setRequestHeader("pragma", "no-cache");
                        req.setRequestHeader("cache-control", "no-cache");
                    },
                },
            },
            schema: {
                data: response => {
                    return response.value.map(l => new OpportunityModel(OpportunityEntity.fromOdata(l, new OpportunityEntity()), this.currencies));
                },
            },
        });
    };

    private getTodoDataSource = (filter: string) => {
        return kendo.data.DataSource.create({
            type: "odata-v4",
            pageSize: this.PAGE_SIZE,
            serverPaging: true,
            serverFiltering: true,
            transport: {
                read: {
                    url:
                        Network.OdataBaseURL +
                        TodoService.ODATA_ROUTE +
                        `?$expand=Persons($select=Id,DisplayName),Institutions($select=Id,Name),PersonToInstitutionRelations($expand=Person($select=Id,DisplayName),Institution($select=Id,Name),Relation)` +
                        `&$filter=${filter}`,
                    beforeSend: req => {
                        req.setRequestHeader("Authorization", this.network.getAuthorizationHeader(this.token));
                        req.setRequestHeader("pragma", "no-cache");
                        req.setRequestHeader("cache-control", "no-cache");
                    },
                },
            },
            schema: {
                data: response => {
                    return response.value.map(t => {
                        const model = new TodoModel(TodoEntity.fromOdata(t, new TodoEntity()));
                        model.IsBoundToMail = this.todoBoundToMail(t.Uuid);
                        return model;
                    });
                },
            },
        });
    };

    private getContactNoteDataSource = (filter: string) => {
        return kendo.data.DataSource.create({
            type: "odata-v4",
            pageSize: this.PAGE_SIZE,
            serverPaging: true,
            serverFiltering: true,
            transport: {
                read: {
                    url:
                        Network.OdataBaseURL +
                        AkquiseService.ODATA_ROUTE +
                        `?$expand=Persons($select=Id,DisplayName),Institutions($select=Id,Name),PersonToInstitutionRelations($expand=Person($select=Id,DisplayName),Institution($select=Id,Name))` +
                        // The backend does not check the lead right on this expand, and a lead shown without its currencies breaks the row.
                        (this.permissions.LeadRead ? `,Lead($expand=Person,Institution,ContactPerson)` : "") +
                        `&$filter=${filter}` +
                        `&$orderby=Date desc`,
                    beforeSend: req => {
                        req.setRequestHeader("Authorization", this.network.getAuthorizationHeader(this.token));
                        req.setRequestHeader("pragma", "no-cache");
                        req.setRequestHeader("cache-control", "no-cache");
                    },
                },
            },
            schema: {
                data: response => {
                    return response.value.map(c => {
                        const entity = ContactNoteEntity.fromOdata(c, new ContactNoteEntity());
                        const model = new ContactNoteModel(
                            entity,
                            this.akquiseTypes.find(a => a.Id === entity.ContactTypeId),
                            entity.Opportunity ? new OpportunityModel(entity.Opportunity, this.currencies) : null
                        );
                        model.IsBoundToMail = this.contactNoteBoundToMail(c.Uuid);
                        return model;
                    });
                },
            },
        });
    };

    private getVerantwortlicheDataSource = () => {
        return kendo.data.DataSource.create({
            type: "odata-v4",
            transport: {
                read: {
                    url: Network.OdataBaseURL + "user" + "?$select=Id,DisplayName",
                    beforeSend: req => {
                        req.setRequestHeader("Authorization", this.network.getAuthorizationHeader(this.token));
                    },
                },
            },
            schema: {
                data: response => {
                    return response.value;
                },
            },
        });
    };

    protected getSearchDataSource = () => {
        return kendo.data.DataSource.create({
            transport: {
                parameterMap: () => {
                    return JSON.stringify(this.getSearchDataSourceParameters());
                },
                read: {
                    url: `${Network.APIBaseURL}${SearchService.API_ROUTE}`,
                    data: this.getSearchDataSourceParameters,
                    contentType: "application/json",
                    dataType: "json",
                    type: "POST",
                    beforeSend: req => {
                        req.setRequestHeader("Authorization", this.network.getAuthorizationHeader(this.token));
                        req.setRequestHeader("pragma", "no-cache");
                        req.setRequestHeader("cache-control", "no-cache");
                    },
                    complete: async (items: JQuery.jqXHR<any>) => {
                        const response: IApiSearchResult[] = items.responseJSON;
                        // "complete" also fires for error responses, and a rejected search carries no result
                        // array - reading it as one would fail inside this callback, where nobody sees it.
                        if (!Array.isArray(response)) {
                            // Handed over as the ApiError it is, so a refusal stays off the log relay like any other.
                            const url = `${Network.APIBaseURL}${SearchService.API_ROUTE}`;
                            this.logger.log(Loglevel.Error, "Search request failed", new ApiError(url, items.status, `request to ${url} failed with status ${items.status}`));
                            return;
                        }
                        const personRelations = await Promise.all(
                            response
                                .filter(sr => sr.EntityTypeId === EntityTypeId.PersonEntity)
                                .map(async sr => {
                                    return this.ptirService.getRelationsByPersonId(sr.EntityId);
                                })
                        );
                        $("#searchListView")
                            .data()
                            .kendoMobileListView.dataSource.data()
                            .forEach(sr => {
                                const relation =
                                    Util.firstOrDefault(personRelations.find(x => x.find(y => y.IsMainInstitution && y.PersonId === sr.EntityId))) ||
                                    Util.firstOrDefault(personRelations.find(x => x.find(y => y.PersonId === sr.EntityId)));
                                if (relation) {
                                    sr.set("RelationName", relation.RelationName);
                                }
                            });
                    },
                },
            },
            schema: {
                data: response => {
                    return response
                        .map((r: IApiSearchResult) => this.searchService.mapDtoToEntity(r))
                        .filter(r => r.EntityTypeId === EntityTypeId.PersonEntity || r.EntityTypeId == EntityTypeId.InstitutionEntity);
                },
            },
        });
    };

    protected getSearchDataSourceParameters = () => {
        return {
            search: this.get("globalFilter"),
            entityTypesToSearch: [EntityTypeId.PersonEntity, EntityTypeId.InstitutionEntity],
        };
    };

    private getFilterForOpportunitiesInTodos = () => {
        if (!this.get("hasAttachedTodoOpportunity") || this.opportunitySearchEnabledForTodos) {
            const filter = {
                logic: "and",
                filters: [
                    {
                        field: "EOverallState",
                        operator: "eq",
                        value: "None",
                    },
                    {
                        logic: "or",
                        filters: [
                            {
                                field: "Titel",
                                operator: "contains",
                                value: this.get("opportunitySearchTodos"),
                            },
                            {
                                field: "Person/DisplayName",
                                operator: "contains",
                                value: this.get("opportunitySearchTodos"),
                            },
                            {
                                field: "Institution/Name",
                                operator: "contains",
                                value: this.get("opportunitySearchTodos"),
                            },
                            {
                                field: "ContactPerson/DisplayName",
                                operator: "contains",
                                value: this.get("opportunitySearchTodos"),
                            },
                        ],
                    },
                ],
            };
            if (this.get("filterInstOpportunities")) {
                filter.filters.push({
                    field: "Institution/Id",
                    operator: "eq",
                    value: this.get<SenderModel>("sender").Institution.Id as any,
                });
            }
            return filter;
        }

        return {
            field: "Id",
            operator: "eq",
            value: this.selectedTodoOpportunityId,
        };
    };

    private getFilterForOpportunitiesInContactNotes = () => {
        if (!this.get("hasAttachedContactOpportunity") || this.opportunitySearchEnabledForContactNotes) {
            const filter = {
                logic: "and",
                filters: [
                    {
                        field: "EOverallState",
                        operator: "eq",
                        value: "None",
                    },
                    {
                        logic: "or",
                        filters: [
                            {
                                field: "Titel",
                                operator: "contains",
                                value: this.get("opportunitySearchContactNotes"),
                            },
                            {
                                field: "Person/DisplayName",
                                operator: "contains",
                                value: this.get("opportunitySearchContactNotes"),
                            },
                            {
                                field: "Institution/Name",
                                operator: "contains",
                                value: this.get("opportunitySearchContactNotes"),
                            },
                            {
                                field: "ContactPerson/DisplayName",
                                operator: "contains",
                                value: this.get("opportunitySearchContactNotes"),
                            },
                        ],
                    },
                ],
            };
            if (this.get("filterInstOpportunities")) {
                filter.filters.push({
                    field: "Institution/Id",
                    operator: "eq",
                    value: this.get<SenderModel>("sender").Institution.Id as any,
                });
            }
            return filter;
        }

        return {
            field: "Id",
            operator: "eq",
            value: this.selectedContactNotesOpportunityId,
        };
    };

    public resetDefaultValues() {
        this.set("version", APP_VERSION);
        this.set("sender", new SenderModel(null, null, null, null, null));
        this.set("relationName", "");
        this.set("createContactNoteText", this.getCreateContactNoteText());
        this.set("globalFilter", "");
        this.set("noSearchResults", false);
        this.set("hasAttachedContactOpportunity", false);
        this.set("opportunitySearchTodos", "");
        this.set("opportunitySearchContactNotes", "");
        this.set("createContactOptionsVisible", false);
        this.set("createContactPhotoVisible", false);
        this.contactCreationService.set("createContactPhotoVisible", false);
        this.set("createContactTextVisible", false);
        this.set("createContactReviewVisible", false);
        this.set("canAnalyzePhoto", false);
        this.set("canAnalyzeText", false);
        this.set("webcamActive", false);
        this.set("parsedPersons", []);
        this.set("parsedInstitutions", []);
        this.set("parsedRelation", null);
        this.set("hasRelation", false);
        this.set("unassignedContactData", []);
        this.set("canCreateContact", false);
    }

    public showSearchView() {
        this.set("globalFilter", "");
        this.set("senderVisible", false);
        this.set("searchVisible", true);
        this.set("createContactOptionsVisible", false);
    }

    public showCreateContactOptions = () => {
        this.set("searchVisible", false);
        this.set("createContactOptionsVisible", true);
    };

    public showCreateFromPhoto = () => {
        this.set("searchVisible", false);
        this.set("createContactOptionsVisible", false);
        this.set("createContactPhotoVisible", true);
        this.contactCreationService.set("createContactPhotoVisible", true);
    };

    public showCreateFromText = () => {
        this.set("searchVisible", false);
        this.set("createContactOptionsVisible", false);
        this.set("createContactTextVisible", true);
        this.contactCreationService.set("createContactTextVisible", true);
    };

    public cancelCreateFromPhoto = () => {
        this.set("createContactPhotoVisible", false);
        this.contactCreationService.cancelCreateFromPhoto();
        this.showCreateContactOptions();
    };

    public cancelCreateFromText = () => {
        this.set("createContactTextVisible", false);
        this.contactCreationService.cancelCreateFromText();
        this.showCreateContactOptions();
    };

    public cancelContactReview = () => {
        this.set("createContactReviewVisible", false);
        this.contactCreationService.cancelContactReview();
        this.showSearchView();
    };

    public searchGlobal = async e => {
        if (this.get("globalFilter") != e.currentTarget.value) {
            clearTimeout(this.searchTimeout);
            this.searchTimeout = setTimeout(() => {
                this.set("globalFilter", e.currentTarget.value);
                this.set("noSearchResults", false);
                kendo.ui.progress($("#searchListView"), true);
                this.get<kendo.data.DataSource>("globalSearchDataSource").read();
            }, 1000);
        }
    };

    public searchOpenOpportunityForContactNotes = e => {
        this.set("opportunitySearchContactNotes", e.currentTarget.value);
        this.opportunitySearchEnabledForContactNotes = true;
        this.get<any>("openOpportunitiesForContactNotes").filter(this.getFilterForOpportunitiesInContactNotes());
    };

    public searchOpenOpportunityForTodos = e => {
        this.set("opportunitySearchTodos", e.currentTarget.value);
        this.opportunitySearchEnabledForTodos = true;
        this.get<any>("openOpportunitiesForTodos").filter(this.getFilterForOpportunitiesInTodos());
    };

    public attachOpportunityToContactNotes = e => {
        this.set("hasAttachedContactOpportunity", true);
        this.selectedContactNotesOpportunityId = e.data.Id;
        this.opportunitySearchEnabledForContactNotes = false;
        this.get<any>("openOpportunitiesForContactNotes").filter(this.getFilterForOpportunitiesInContactNotes());
    };

    public detachOpportunityForContactNotes = e => {
        this.set("hasAttachedContactOpportunity", false);
        this.selectedContactNotesOpportunityId = null;
        this.opportunitySearchEnabledForContactNotes = false;
        this.get<any>("openOpportunitiesForContactNotes").filter(this.getFilterForOpportunitiesInContactNotes());
    };

    public attachTodoOpportunity = e => {
        this.set("hasAttachedTodoOpportunity", true);
        this.selectedTodoOpportunityId = e.data.Id;
        this.opportunitySearchEnabledForTodos = false;
        this.get<any>("openOpportunitiesForTodos").filter(this.getFilterForOpportunitiesInTodos());
    };

    public detachTodoOpportunity = e => {
        this.set("hasAttachedTodoOpportunity", false);
        this.selectedTodoOpportunityId = null;
        this.opportunitySearchEnabledForTodos = false;
        this.get<any>("openOpportunitiesForTodos").filter(this.getFilterForOpportunitiesInTodos());
    };

    private relationChooser = async (ptir: PersonToInstitutionRelationModel[]): Promise<PersonToInstitutionRelationModel> => {
        if (!ptir?.length) {
            // Only a click on an entry resolves the promise below, so an empty list would leave the user
            // in a chooser they cannot get out of.
            return null;
        }
        if (ptir.length === 1) {
            return Util.firstOrDefault(ptir);
        }
        this.set("relationChooserVisible", true);
        this.set("showContent", true);
        $("#tabs").data("kendoTabStrip").select(0);
        this.set("senderVisible", false);
        const $lv = $("#relationListView");
        const existingLv = $lv.data("kendoMobileListView");
        if (existingLv) {
            existingLv.destroy();
            $lv.empty();
        }

        for (const p of ptir) {
            p.Avatar = await this.avatarService.getAvatar(p.PersonId, EntityTypeId.PersonEntity);
        }

        return new Promise(res => {
            $("#relationListView")
                .kendoMobileListView({
                    dataSource: ptir,
                    template: $("#relationChooserTemplate").html(),
                    click: e => {
                        this.set("relationChooserVisible", false);
                        res(e.dataItem);
                    },
                })
                .data().kendoMobileListView;
            Util.hideLoading();
        });
    };

    public createNote = async () => {
        if (this.contactNotevalidator.validate()) {
            Util.showLoading();
            ($("#orphySaveMail") as any).modal("toggle");
            const sender = this.getSender();
            // no selected opportunity but an opportunity to create
            if (
                this.permissions.LeadCreate &&
                !this.selectedContactNotesOpportunityId &&
                this.get("contactNoteOpportunityCreateTitle") &&
                this.get("contactNoteOpportunityCreateVerantwortlich")
            ) {
                let opportunity = await this.createOpportunityEntityFromContactNote(sender);
                if (!opportunity) {
                    this.reportNotSaved("Unable to create the opportunity of a contact note");
                    return;
                }
                this.selectedContactNotesOpportunityId = opportunity.Id;
            }

            let contactNote = await this.createContactNoteEntity();
            if (!contactNote) {
                this.reportNotSaved("Unable to create a contact note");
                return;
            }

            let hasAttachmentError = await this.handleAttachmentUpload(contactNote.Uuid, EntityTypeId.Akquise);

            await this.createNoteRefLinks(sender, contactNote);

            await this.addContactNoteCategory(contactNote.Uuid);

            this.toggleCreateContactNote();

            await this.refreshPlugin();

            this.showContactNoteCreatedNotification(hasAttachmentError);
        }
    };

    public createTodo = async () => {
        if (this.todoValidator.validate()) {
            Util.showLoading();
            ($("#orphyTodo") as any).modal("toggle");
            const sender = this.getSender();

            // no selected opportunity but an opportunity to create
            if (this.permissions.LeadCreate && !this.selectedTodoOpportunityId && this.get("todoOpportunityCreateTitle") && this.get("todoOpportunityCreateVerantwortlich")) {
                let opportunity = await this.createOpportunityEntityFromTodo(sender);
                if (!opportunity) {
                    this.reportNotSaved("Unable to create the opportunity of a todo");
                    return;
                }
                this.selectedTodoOpportunityId = opportunity.Id;
            }

            let todo = await this.createTodoEntity();
            if (!todo) {
                this.reportNotSaved("Unable to create a todo");
                return;
            }

            let hasAttachmentError = await this.handleAttachmentUpload(todo.Uuid, EntityTypeId.TodoEntity);
            this.toggleCreateTodo();
            await this.createTodoRefLinks(sender, todo);

            await this.addTodoCategory(todo.Uuid);

            await this.refreshPlugin();
            this.showTodoCreatedNotification(hasAttachmentError);
        }
    };

    private setNewLinks = async (email: string) => {
        this.set(
            "newContactLink",
            email ? `${Network.BaseURL}PersonDetail/NewFromEmail?email=${email}` : `${Network.BaseURL}PersonDetail/Details/0?editMode=True`
        );
        this.set(
            "newOpportunityLink",
            email ? `${Network.BaseURL}OpportunityDetail/NewFromEmail?email=${email}` : `${Network.BaseURL}OpportunityDetail/Details/0?editMode=True`
        );
        this.set(
            "newTodoLink",
            email ? `${Network.BaseURL}TodoDetail/NewFromEmail?email=${email}` : `${Network.BaseURL}TodoDetail/Details/0?editMode=True`
        );
    };

    public filterInstitutionOpportunitiesForTodos = e => {
        this.set("filterInstOpportunities", e.checked);
        this.opportunitySearchEnabledForTodos = true;
        this.get<any>("openOpportunitiesForTodos").filter(this.getFilterForOpportunitiesInTodos());
    };

    public filterInstitutionOpportunitiesForContactNotes = e => {
        this.set("filterInstOpportunities", e.checked);
        this.opportunitySearchEnabledForContactNotes = true;
        this.get<any>("openOpportunitiesForContactNotes").filter(this.getFilterForOpportunitiesInContactNotes());
    };

    public instFilterVisible = (): boolean => {
        return this.get("sender") && this.get<SenderModel>("sender").HasInstitution && !this.get("hasAttachedContactOpportunity");
    };

    public instFilterTodoVisible = (): boolean => {
        return this.get("sender") && this.get<SenderModel>("sender").HasInstitution && !this.get("hasAttachedTodoOpportunity");
    };

    public async selectPersons() {
        if (this.get<SenderModel>("sender").HasInstitution) {
            let relations = await this.ptirService.getRelationsByInstitutionId(this.get<SenderModel>("sender").Institution.Id);
            let result = await this.relationChooser(relations);
            if (result) {
                this.load(null, result.PersonId, result.InstitutionId);
            }
        }
    }

    public async selectInstitutions() {
        if (this.get<SenderModel>("sender").HasPerson) {
            let relations = await this.ptirService.getRelationsByPersonId(this.get<SenderModel>("sender").Person.Id);
            let result = await this.relationChooser(relations);
            if (result) {
                this.load(null, result.PersonId, result.InstitutionId);
            }
        }
    }

    public async selectPerson() {
        if (this.get<SenderModel>("sender").HasPerson) {
            await this.load(null, this.get<SenderModel>("sender").Person.Id, null);
        }
    }

    public async selectInstitution() {
        if (this.get<SenderModel>("sender").HasInstitution) {
            await this.load(null, null, this.get<SenderModel>("sender").Institution.Id);
        }
    }

    private async loadData(emailAddress: string, personId?: number, institutionId?: number) {
        if (institutionId && personId) {
            const ptirs = await this.ptirService.getRelationByPersonInstitutionId(personId, institutionId);
            let ptir: PersonToInstitutionRelationModel;
            if (ptirs.length > 1) {
                ptir = await this.relationChooser(ptirs);
            } else {
                ptir = Util.firstOrDefault(ptirs);
            }
            if (ptir) {
                await this.loadPersonInstitutionRelationData(ptir);
            } else {
                // The relation could not be read - the institution is still the better half to show. Saying so
                // keeps a note or todo from being filed against the institution alone without the user noticing.
                await this.loadInstitutionData(institutionId);
                this.showNotification(BaseModel.t("notification-relation-not-loaded"), "warning");
            }
        } else if (institutionId) {
            await this.loadInstitutionData(institutionId);
        } else if (personId) {
            await this.loadPersonData(personId);
        } else if (emailAddress) {
            let ptir: PersonToInstitutionRelationModel;
            const ptirs = await this.ptirService.getPersonToInstitutionRelationsByEmail(emailAddress);
            if (ptirs.length > 1) {
                ptir = await this.relationChooser(ptirs);
            } else {
                ptir = Util.firstOrDefault(ptirs);
            }
            if (!!ptir) {
                await this.loadPersonInstitutionRelationData(ptir);
            } else {
                const instId = await this.institutionService.findInstitutionByEmail(emailAddress);
                if (instId) {
                    await this.loadInstitutionData(instId);
                } else {
                    const personId = await this.personService.findPersonByEmailAddress(emailAddress);
                    if (personId) {
                        await this.loadPersonData(personId);
                    }
                }
            }
        }
    }

    private async loadPersonData(personId: number) {
        this.set("sender", await this.senderModelService.getPersonSenderModelById(personId));
        let opportunityQuery = `PersonId eq ${personId}`;
        let todoQuery = `Persons/any(p:p/Id eq ${personId})`;
        let contactNoteQuery = `Persons/any(i:i/Id eq ${personId})`;
        this.loadRelatedOpportunitiesTodosAndContactNotes(opportunityQuery, todoQuery, contactNoteQuery);
    }

    private async loadInstitutionData(instId: number) {
        this.set("sender", await this.senderModelService.getInstitutionSenderModel(instId));
        let opportunityQuery = `InstitutionId eq ${instId}`;
        let todoQuery = `Institutions/any(i: i/Id eq ${instId})`;
        let contactNoteQuery = `Institutions/any(i:i/Id eq ${instId})`;
        this.loadRelatedOpportunitiesTodosAndContactNotes(opportunityQuery, todoQuery, contactNoteQuery);
    }

    private async loadPersonInstitutionRelationData(ptir: PersonToInstitutionRelationModel) {
        this.set("sender", await this.senderModelService.getSenderModel(ptir.PersonId, ptir.InstitutionId));
        let opportunityQuery = `PersonId eq ${ptir.PersonId} and InstitutionId eq ${ptir.InstitutionId}`;
        let todoQuery = `PersonToInstitutionRelations/any(pr:pr/PersonId eq ${ptir.PersonId} and pr/InstitutionId eq ${ptir.InstitutionId})`;
        let contactNoteQuery = `PersonToInstitutionRelations/any(pr:pr/PersonId eq ${ptir.PersonId} and pr/InstitutionId eq ${ptir.InstitutionId})`;
        this.set("relationName", ptir.RelationName);
        this.loadRelatedOpportunitiesTodosAndContactNotes(opportunityQuery, todoQuery, contactNoteQuery);
    }

    private loadRelatedOpportunitiesTodosAndContactNotes(opportunityQuery: string, todoQuery: string, contactNoteQuery: string) {
        if (!!this.get("sender")) {
            this.set("sender.Opportunities", this.dataSourceIf(this.permissions.LeadRead, () => this.getOpportunityDataSource(opportunityQuery)));
            this.set("sender.Todos", this.dataSourceIf(this.permissions.TodoRead, () => this.getTodoDataSource(todoQuery)));
            this.set("sender.ContactNotes", this.dataSourceIf(this.permissions.ContactNoteRead, () => this.getContactNoteDataSource(contactNoteQuery)));
        }
    }

    /**
     * The list behind a right, or an empty local one where the user may not read it: the widgets bound to it -
     * and their filters - keep working without a request the backend would refuse.
     */
    private dataSourceIf = (mayRead: boolean, create: () => kendo.data.DataSource): kendo.data.DataSource => {
        return mayRead ? create() : new kendo.data.DataSource({ data: [], pageSize: this.PAGE_SIZE });
    };

    public listviewDatabound() {
        BaseModel.localize();
    }
    private async createOpportunityEntityFromContactNote(sender: SenderModel) {
        let opportunity = new OpportunityEntity();
        opportunity.Titel = this.get("contactNoteOpportunityCreateTitle");
        opportunity.ContactPersonId = this.get("contactNoteOpportunityCreateVerantwortlich");
        opportunity.CurrencyId = this.currencies.find(r => r.IsMainCurrency).Id;
        if (this.get("contactNoteOpportunityCreatePrice")) {
            opportunity.Price = parseFloat(this.get("contactNoteOpportunityCreatePrice"));
        } else {
            opportunity.Price = 0.0;
        }

        opportunity.DueDate = this.get("contactNoteOpportunityCreateDueDate");

        if (sender.HasPerson) {
            opportunity.PersonId = sender.Person.Id;
        }
        if (sender.HasInstitution) {
            opportunity.InstitutionId = sender.Institution.Id;
        }

        opportunity = await this.opportunityService.save(opportunity);
        return opportunity;
    }

    private async createOpportunityEntityFromTodo(sender: SenderModel): Promise<OpportunityEntity> {
        let opportunity = new OpportunityEntity();
        opportunity.Titel = this.get("todoOpportunityCreateTitle");
        opportunity.ContactPersonId = this.get("todoOpportunityCreateVerantwortlich");
        opportunity.CurrencyId = this.currencies.find(r => r.IsMainCurrency).Id;
        if (this.get("todoOpportunityCreatePrice")) {
            opportunity.Price = parseFloat(this.get("todoOpportunityCreatePrice"));
        } else {
            opportunity.Price = 0.0;
        }
        opportunity.DueDate = this.get("todoOpportunityCreateDueDate");

        if (sender.HasPerson) {
            opportunity.PersonId = sender.Person.Id;
        }
        if (sender.HasInstitution) {
            opportunity.InstitutionId = sender.Institution.Id;
        }
        opportunity = await this.opportunityService.save(opportunity);
        return opportunity;
    }

    protected async createTodoEntity(): Promise<TodoEntity> {
        let todo = new TodoEntity();
        todo.Titel = this.get("newTodoTitle");
        todo.Priority = this.get("todoPriority");
        todo.TodoOwners = this.get("todoVerantwortlicheSelected");
        todo.Start = this.get("todoStartDateTime");
        todo.End = this.get("todoEndDateTime");
        todo.Beschreibung = this.get<string>("todoDescription").replace(/[\n]+/gm, "<br/>");
        todo.OpportunityId = this.selectedTodoOpportunityId;
        todo.ReminderEnabled = this.get("reminderActive");
        if (todo.ReminderEnabled) {
            todo.ReminderSeconds = this.get<number>("reminderSeconds") * this.get<number>("reminderFactorValue");
        }
        todo = await this.todoService.save(todo);
        return todo;
    }

    protected async createContactNoteEntity(id?: number): Promise<ContactNoteEntity> {
        let contactNote = new ContactNoteEntity();
        contactNote.Id = id;
        contactNote.Subject = this.get("newNotesubject");
        contactNote.Date = new Date(this.get("newNoteDateTime")).toISOString();
        contactNote.Mood = this.get("newNoteMood");
        contactNote.ContactTypeId = parseInt(this.get("newNoteAkquiseTypeId"));
        contactNote.Note = this.get<string>("newNoteNote").replace(/[\n]+/gm, "<br/>");
        contactNote.OpportunityId = this.selectedContactNotesOpportunityId;
        contactNote = await this.contactNoteService.save(contactNote);
        return contactNote;
    }

    public hideSearchGroup = e => {
        const headerElement = $(e.target).closest(".searchListViewHeaderSelector");
        const list = $(`.${headerElement.data("kind")}`).closest(".km-list");
        const icon = headerElement.find(".listVisibleIndicator");
        list.toggle();
        if (list.is(":visible")) {
            icon.removeClass("fa-chevron-down");
            icon.addClass("fa-chevron-up");
        } else {
            icon.removeClass("fa-chevron-up");
            icon.addClass("fa-chevron-down");
        }
    };

    public manualSelectContact = async (e): Promise<void> => {
        const listView = e.sender.element.data("kendoMobileListView");
        const dataItem = listView.dataSource.view()[e.item.index()];
        if (dataItem) {
            this.set("searchVisible", false);
            if (dataItem.EntityTypeId === EntityTypeId.InstitutionEntity) {
                await this.load(null, null, dataItem.EntityId, false);
            } else if (dataItem.EntityTypeId === EntityTypeId.PersonEntity) {
                let privateRelation = new PersonToInstitutionRelationEntity();
                privateRelation.InstitutionId = null;
                privateRelation.PersonId = dataItem.EntityId;
                privateRelation.Relation = new PersonToInstRelationTypeEntity({
                    TranslatedDisplayName: "",
                });
                privateRelation.InstitutionDisplayName = "";
                privateRelation.PersonDisplayName = dataItem.Text;

                let relations = await this.ptirService.getRelationsByPersonId(dataItem.EntityId);
                relations.push(new PersonToInstitutionRelationModel(privateRelation, await this.contactMediaTypeService.getItems()));
                let institutionId = null;
                if (relations && relations.length > 1) {
                    const ptir = await this.relationChooser(relations);
                    await this.load(null, ptir.PersonId, ptir.InstitutionId, false);
                } else {
                    await this.load(null, dataItem.EntityId, institutionId, false);
                }
            }
        }
    };

    public searchListViewDataBound = async e => {
        for (const item of e.sender.dataSource.data()) {
            item.set("RenderImage", await this.avatarService.getAvatar(item.EntityId, item.EntityTypeId));
        }
        kendo.ui.progress($("#searchListView"), false);

        // Set noSearchResults based on whether there are any results
        const hasResults = e.sender.dataSource.data().length > 0;
        this.set("noSearchResults", !hasResults);
    };

    protected async setInitialTodoGuiValues() {
        this.set("newTodoTitle", await this.getSubject());
        this.set("todoPriority", "Prio0");
        this.set("todoVerantwortlicheSelected", []);
        this.set("todoAttachementsSelected", []);
        this.set("todoStartDateTime", await this.getMailboxItemStartDate());
        this.set("todoEndDateTime", await this.getMailboxItemEndDate());
        this.set("todoDescription", "");
        this.set("reminderActive", false);
        this.set(
            "reminderFactorValue",
            this.get<{ name: string; factor: number }[]>("reminderFactor").find(x => x.name === BaseModel.t("day", { count: 0 })).factor
        );
        this.set("reminderSeconds", 0);
    }

    private async setInitialOpportunityGuiValues() {
        let userId = (await this.getMe()).UserId;
        this.set("todoOpportunityCreateTitle", "");
        this.set("todoOpportunityCreateVerantwortlich", userId);
        this.set("todoOpportunityCreatePrice", "0.00");
        this.set("todoOpportunityCreateDueDate", "");

        this.set("contactNoteOpportunityCreateTitle", "");
        this.set("contactNoteOpportunityCreateVerantwortlich", userId);
        this.set("contactNoteOpportunityCreatePrice", "0.00");
        this.set("contactNoteOpportunityCreateDueDate", "");
    }

    protected async setInitialContactNotesGuiValues() {
        this.set("newNotesubject", await this.getSubject());
        this.set("newNoteMood", "Happy");
        this.set("newNoteAkquiseTypeId", 4); //default to email
        this.set("newNoteNote", "");
        this.set("attachementsSelected", []);
        this.set("newNoteDateTime", await this.getMailboxItemStartDate());
    }

    protected async getMailboxItemStartDate(): Promise<Date> {
        return new Date();
    }

    protected async getMailboxItemEndDate(): Promise<Date | string> {
        return "";
    }

    /**
     * A save that did not happen. The services answer a refused write with undefined, and reading an entity
     * out of that used to fail on the next line: inside a click handler nobody catches, which left the
     * spinner of a closed dialog running for good and the typed text lost without a word.
     *
     * The reason is in the log already, so this only has to end the flow it interrupted: stop the spinner
     * and say that nothing was saved.
     */
    protected reportNotSaved(reason: string) {
        Util.hideLoading();
        this.logger.log(Loglevel.Warning, `${reason}: the save was refused, so the flow was ended`);
        this.showNotification(BaseModel.t("error-not-saved"), "warning");
    }

    protected showContactNoteCreatedNotification(hasAttachmentError: boolean) {
        if (hasAttachmentError) {
            this.showNotification(BaseModel.t("notification-contact-note-saved-attachments-not-supportet"), "warning");
        } else {
            this.showNotification(BaseModel.t("notification-contact-note-saved"), "success");
        }
    }

    protected showTodoCreatedNotification(hasAttachmentError: boolean) {
        if (hasAttachmentError) {
            this.showNotification(BaseModel.t("notification-todo-saved-attachments-not-supportet"), "warning");
        } else {
            this.showNotification(BaseModel.t("notification-todo-saved"), "success");
        }
    }

    protected showNotification = (message: string, level: "warning" | "success") => {
        $("#popupNotification")
            .kendoNotification({
                autoHideAfter: 3000,
                position: { top: 35, left: 0 },
                width: $("body").width(),
            })
            .data("kendoNotification")
            .show(message, level);
    };

    protected async createTodoRefLinks(sender: SenderModel, todo: TodoEntity) {
        if (sender.HasInstitution && sender.HasPerson) {
            await this.todoService.linkPersonToInstitutionEntry(todo.Id, sender.Person.Id, sender.Institution.Id);
        } else if (sender.HasInstitution) {
            await this.todoService.linkInstitutionEntry(todo.Id, sender.Institution.Id);
        } else if (sender.HasPerson) {
            await this.todoService.linkPersonEntry(todo.Id, sender.Person.Id);
        }
    }

    protected async createNoteRefLinks(sender: SenderModel, contactNote: ContactNoteEntity) {
        if (sender.HasInstitution && sender.HasPerson) {
            await this.contactNoteService.linkPersonToInstitutionEntry(contactNote.Id, sender.Person.Id, sender.Institution.Id);
        } else if (sender.HasInstitution) {
            await this.contactNoteService.linkInstitutionEntry(contactNote.Id, sender.Institution.Id);
        } else if (sender.HasPerson) {
            await this.contactNoteService.linkPersonEntry(contactNote.Id, sender.Person.Id);
        }
    }

    protected getSender = (): SenderModel => {
        return this.get("sender") as SenderModel;
    };
}
